import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  SiSlack,
  SiGmail,
  SiGooglesheets,
  SiNotion,
} from "react-icons/si";
import { TbBrandOffice } from "react-icons/tb";
import {
  CheckCircle,
  ArrowSquareOut,
  Lightning,
  Globe,
} from "@phosphor-icons/react";

const ICON_MAP = {
  SLACK: SiSlack,
  GMAIL: SiGmail,
  GOOGLESHEETS: SiGooglesheets,
  OUTLOOK: TbBrandOffice,
  NOTION: SiNotion,
  FIRECRAWL: Globe,
};

const COLOR = {
  SLACK: "#4A154B",
  GMAIL: "#C94A3D",
  GOOGLESHEETS: "#0F9D58",
  OUTLOOK: "#0078D4",
  NOTION: "#000",
  FIRECRAWL: "#FF6F00",
};

const IntegrationsPage = () => {
  const [items, setItems] = useState([]);
  const [logs, setLogs] = useState([]);
  const [running, setRunning] = useState(null);

  const load = async () => {
    const [t, l] = await Promise.all([
      api.get("/composio/toolkits"),
      api.get("/composio/logs"),
    ]);
    setItems(t.data.items);
    setLogs(l.data.items);
  };

  useEffect(() => {
    load();
  }, []);

  const connect = async (slug) => {
    try {
      const r = await api.post(`/composio/${slug}/connect`);
      window.open(r.data.redirect_url, "_blank", "noopener");
      toast.message("Complete OAuth in the new tab, then mark as connected.");
    } catch {
      toast.error("Could not start connection");
    }
  };

  const markConnected = async (slug) => {
    await api.post(`/composio/${slug}/mark-connected`);
    toast.success(`${slug} connected.`);
    load();
  };

  const runDemo = async (toolkit) => {
    setRunning(toolkit);
    try {
      const presets = {
        SLACK: {
          action: "SLACK_POST_MESSAGE",
          params: { channel: "#engagements", text: "New engagement created — please review." },
        },
        GMAIL: {
          action: "GMAIL_SEND_MESSAGE",
          params: {
            to: "client@example.com",
            subject: "Welcome to Ledgerline",
            body: "Welcome aboard.",
          },
        },
        GOOGLESHEETS: {
          action: "GOOGLESHEETS_READ_RANGE",
          params: { spreadsheet_id: "1ABC...XYZ", range: "Clients!A1:D50" },
        },
        OUTLOOK: {
          action: "OUTLOOK_CREATE_EVENT",
          params: {
            subject: "Client Onboarding",
            start: new Date(Date.now() + 86400000).toISOString(),
            end: new Date(Date.now() + 90000000).toISOString(),
            attendees: ["client@example.com"],
          },
        },
        NOTION: {
          action: "NOTION_CREATE_PAGE",
          params: { parent: "Engagements", title: "New Engagement — Acme Co." },
        },
        FIRECRAWL: {
          action: "FIRECRAWL_SCRAPE_EXTRACT_DATA_LLM",
          params: { url: "https://www.irs.gov/forms-pubs", formats: ["json"] },
        },
      };
      const preset = presets[toolkit] || { action: "GENERIC", params: {} };
      const r = await api.post("/composio/execute", { toolkit, ...preset });
      toast.success(`Executed ${preset.action}`);
      console.log("Integration result:", r.data);
      load();
    } catch {
      toast.error("Action failed");
    } finally {
      setRunning(null);
    }
  };

  return (
    <div data-testid="integrations-page" className="space-y-8">
      <div className="pb-8 border-b border-[#1A2F24]/12">
        <p className="label-eyebrow">Connected Apps</p>
        <h1 className="font-serif text-4xl md:text-5xl text-[#112018] mt-2">Integrations</h1>
        <p className="text-[#606C64] mt-2 text-sm max-w-xl">
          Connect the apps your firm already uses. We handle authentication and provide
          access to 500+ tools — six of the most common are surfaced here.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {items.map((tk) => {
          const Icon = ICON_MAP[tk.slug] || Lightning;
          return (
            <div key={tk.slug} className="card-flat" data-testid={`integration-${tk.slug}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <Icon size={32} color={COLOR[tk.slug] || "#1A2F24"} />
                  <div>
                    <p className="font-serif text-xl text-[#112018]">{tk.name}</p>
                    <p className="label-eyebrow mt-1">{tk.slug}</p>
                  </div>
                </div>
                {tk.connected && (
                  <CheckCircle size={20} weight="fill" className="text-[#1A2F24]" />
                )}
              </div>
              <p className="text-sm text-[#606C64] mt-4 leading-relaxed">
                {tk.description}
              </p>
              <div className="flex gap-2 mt-6">
                {!tk.connected ? (
                  <>
                    <button
                      onClick={() => connect(tk.slug)}
                      className="btn-primary flex-1"
                      data-testid={`connect-${tk.slug}`}
                    >
                      Connect <ArrowSquareOut size={12} className="inline ml-1" />
                    </button>
                    <button
                      onClick={() => markConnected(tk.slug)}
                      className="btn-outline"
                      title="Mark as connected (demo)"
                      data-testid={`mark-connected-${tk.slug}`}
                    >
                      ✓
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => runDemo(tk.slug)}
                    disabled={running === tk.slug}
                    className="btn-primary flex-1"
                    data-testid={`run-${tk.slug}`}
                  >
                    {running === tk.slug ? "Running…" : "Run Demo Action"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {logs.length > 0 && (
        <div className="card-flat">
          <p className="label-eyebrow">Activity</p>
          <h2 className="font-serif text-2xl text-[#112018] mt-1">Recent integration actions</h2>
          <div className="mt-4 divide-y divide-[#1A2F24]/12">
            {logs.slice(0, 10).map((l) => (
              <div key={l.id} className="py-3 grid md:grid-cols-[140px_1fr_auto] gap-3">
                <span className="font-mono text-xs text-[#A8977B] uppercase">
                  {l.toolkit}
                </span>
                <span className="font-mono text-xs text-[#1A2F24]">{l.action}</span>
                <span className="font-mono text-xs text-[#606C64]">
                  {new Date(l.at).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationsPage;
